/**
 * Export Buttons Component
 * Provides PDF and CSV export buttons for statistics pages
 * Respects role-based permissions - only managers and owners can export
 * Other roles see disabled buttons
 */

import React, { useState, useEffect } from 'react';
import { normalizeUser } from '@/services/supabase';
import PdfExportButton from '@/utils/PdfExport';
import CsvExportButton from '@/utils/CsvExport';
import type { ExportableData } from '@/utils/export';
import { AppColors } from '@/utils/theme';
import type { BusinessRole } from '@/types/roles';
import { BUSINESS_ROLE_PERMISSIONS } from '@/types/roles';
import { supabase } from '@/services/supabase';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';

interface ExportButtonsProps {
  data: any;
  title: string;
  subtitle?: string;
  type: 'business-review' | 'charity-review' | 'social-impact' | 'analytics';
  filename?: string;
}

const ExportButtons: React.FC<ExportButtonsProps> = ({
  data,
  title,
  subtitle,
  type,
  filename = 'report'
}) => {
  const exportData: ExportableData = {
    title,
    subtitle,
    date: new Date().toLocaleDateString(),
    data
  };

  const { user } = useAuthV2();
  const [canExport, setCanExport] = useState(false);

  useEffect(() => {
    const checkExportPermission = async () => {
      // Get current user metadata from Supabase
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setCanExport(false);
        return;
      }
      
      // Use normalizeUser to create a consistent user object
      if (session.user) {
        console.debug('Using normalizeUser in ExportButtons checkExportPermission');
        const normalizedUser = normalizeUser(session.user);
        
        // Check if user is super admin using normalized user
        const isSuperAdmin = normalizedUser.role === 'super_admin';
        
        if (isSuperAdmin) {
          setCanExport(true);
          return;
        }
        
        // Get business role from normalized user or use staff as default
        const userRole = normalizedUser.businessStaffRole as BusinessRole || 
                        normalizedUser.role as BusinessRole || 
                        'staff';
                        
        // Keep original implementation for backward compatibility
        const originalIsSuperAdmin = session.user?.user_metadata?.role === 'super_admin';
        
        if (originalIsSuperAdmin) {
          setCanExport(true);
          return;
        }
        
        // Get business role from user metadata or use staff as default (original implementation)
        
        // Check if user has export permissions based on role
        setCanExport(BUSINESS_ROLE_PERMISSIONS[userRole]?.canExportReports ?? false);
      }
    };
    
    checkExportPermission();
  }, [user]);

  // Styles for disabled buttons
  const disabledStyle = {
    backgroundColor: '#9CA3AF', // Gray color for disabled buttons
    borderRadius: '0.5rem',
    cursor: 'not-allowed',
    opacity: 0.7
  };

  return (
    <div className="flex space-x-2">
      {canExport ? (
        // Enabled buttons for users with export permissions
        <>
          <CsvExportButton
            data={data}
            filename={filename}
            dataType={type}
          >
            <button
              className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                backgroundColor: AppColors.skyBlue,
                borderRadius: '0.5rem',
              }}
            >
              CSV
            </button>
          </CsvExportButton>

          <PdfExportButton
            data={exportData}
            filename={filename}
            documentType={type}
          >
            <button
              className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                backgroundColor: AppColors.navyBlue,
                borderRadius: '0.5rem',
              }}
            >
              PDF
            </button>
          </PdfExportButton>
        </>
      ) : (
        // Disabled buttons for users without export permissions
        <>
          <div title="Only managers and owners can export reports">
            <button
              className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none"
              style={disabledStyle}
              disabled
            >
              CSV
            </button>
          </div>

          <div title="Only managers and owners can export reports">
            <button
              className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none"
              style={disabledStyle}
              disabled
            >
              PDF
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ExportButtons;
